import React, { FunctionComponent, useState, useEffect } from 'react';
import {
  Answers,
  FelineGrimaceScaleField,
  FelineGrimaceScaleFieldSchema,
  FelineGrimaceScaleValue,
  FelineGrimaceScaleValueSchema
} from '../dynamicFormSchema';
import { DynamicFieldProps } from '../DynamicField';
import { DynamicFieldComponent } from './DynamicFieldComponentBase';
import { Label } from '../../Label';
import { RadioGroupWithOther } from '../../RadioGroupWithOther';
import { useDeepCompareEffect } from '@companion-professional/webutils';

// FelineGrimaceScaleFieldComponent is a custom dynamic field component that has all the entries for the Feline Grimace
// Scale and does the calculations for the total grimace score based on the selected values.
export const FelineGrimaceScaleFieldComponent: FunctionComponent<DynamicFieldProps> = ({
  field,
  reactHookField,
  onChange
}) => {
  const g = FelineGrimaceScaleFieldSchema.parse(field);
  const [earPosition, setEarPosition] = useState<string | undefined>();
  const [orbitalTightening, setOrbitalTightening] = useState<string | undefined>();
  const [muzzleTension, setMuzzleTension] = useState<string | undefined>();
  const [whiskerChange, setWhiskerChange] = useState<string | undefined>();
  const [headPosition, setHeadPosition] = useState<string | undefined>();
  const [grimaceScore, setGrimaceScore] = useState<string | undefined>();

  useDeepCompareEffect(() => {
    // Update the local state only if the value has changed
    setEarPosition(reactHookField.value?.ear_position?.toString());
    setOrbitalTightening(reactHookField.value?.orbital_tightening?.toString());
    setMuzzleTension(reactHookField.value?.muzzle_tension?.toString());
    setWhiskerChange(reactHookField.value?.whisker_change?.toString());
    setHeadPosition(reactHookField.value?.head_position?.toString());
    setGrimaceScore(reactHookField.value?.grimace_score?.toString());
  }, [reactHookField.value]);

  useEffect(() => {
    const earPositionScore = earPosition ? parseInt(earPosition, 10) : undefined;
    const orbitalTighteningScore = orbitalTightening ? parseInt(orbitalTightening, 10) : undefined;
    const muzzleTensionScore = muzzleTension ? parseInt(muzzleTension, 10) : undefined;
    const whiskerChangeScore = whiskerChange ? parseInt(whiskerChange, 10) : undefined;
    const headPositionScore = headPosition ? parseInt(headPosition, 10) : undefined;

    // Calculate the total grimace score
    const totalScore =
      (earPositionScore || 0) +
      (orbitalTighteningScore || 0) +
      (muzzleTensionScore || 0) +
      (whiskerChangeScore || 0) +
      (headPositionScore || 0);

    onChange({
      ear_position: earPositionScore,
      orbital_tightening: orbitalTighteningScore,
      muzzle_tension: muzzleTensionScore,
      whisker_change: whiskerChangeScore,
      head_position: headPositionScore,
      grimace_score: totalScore
    });

    setGrimaceScore(totalScore.toString());
  }, [earPosition, orbitalTightening, muzzleTension, whiskerChange, headPosition]);

  return (
    <div>
      <Label description={g.description}>{g.label}</Label>
      <div className="mt-4 flex flex-col gap-4 rounded-lg border p-2">
        <div>
          <RadioGroupWithOther
            label="Ear position"
            value={earPosition}
            onChange={(value) => {
              setEarPosition(value ? String(value) : undefined);
            }}
            name="ear_position"
            options={[
              { value: '0', label: 'Ears forward, relaxed, or slightly to the side' },
              { value: '1', label: 'Ears slightly flattened or rotated outwards' },
              { value: '2', label: 'Ears flattened and rotated outwards' }
            ]}
          />
        </div>
        <div>
          <RadioGroupWithOther
            label="Orbital tightening"
            value={orbitalTightening}
            onChange={(value) => {
              setOrbitalTightening(value ? String(value) : undefined);
            }}
            name="orbital_tightening"
            options={[
              { value: '0', label: 'Eyes wide open' },
              { value: '1', label: 'Slightly narrowed, partial closing' },
              { value: '2', label: 'Squinting, tight closing' }
            ]}
          />
        </div>
        <div>
          <RadioGroupWithOther
            label="Muzzle tension"
            value={muzzleTension}
            onChange={(value) => {
              setMuzzleTension(value ? String(value) : undefined);
            }}
            name="muzzle_tension"
            options={[
              { value: '0', label: 'Muzzle round' },
              { value: '1', label: 'Slightly tense, forming an elliptical shape' },
              { value: '2', label: 'Tense, forming a triangular shape' }
            ]}
          />
        </div>
        <div>
          <RadioGroupWithOther
            label="Whisker change"
            value={whiskerChange}
            onChange={(value) => {
              setWhiskerChange(value ? String(value) : undefined);
            }}
            name="whisker_change"
            options={[
              { value: '0', label: 'Whiskers loose and curved' },
              { value: '1', label: 'Whiskers straight or slightly pulled back' },
              { value: '2', label: 'Whiskers pulled back and bunched' }
            ]}
          />
        </div>
        <div>
          <RadioGroupWithOther
            label="Head position"
            value={headPosition}
            onChange={(value) => {
              setHeadPosition(value ? String(value) : undefined);
            }}
            name="head_position"
            options={[
              { value: '0', label: 'Head above shoulder line, alert' },
              { value: '1', label: 'Head in line with shoulder line' },
              { value: '2', label: 'Head below shoulder line, tucked into chest' }
            ]}
          />
        </div>
        <div className="flex flex-col gap-2 border-t pt-4 font-semibold">
          <div>Total Points</div>
          <div data-testid={`${g.name}_total`}>{grimaceScore}</div>
        </div>
      </div>
    </div>
  );
};

export class DynamicFelineGrimaceScaleField extends DynamicFieldComponent<
  FelineGrimaceScaleField,
  FelineGrimaceScaleValue
> {
  Component = FelineGrimaceScaleFieldComponent;

  constructor(field: FelineGrimaceScaleField) {
    super(field);
  }

  getAnswer(answers: Answers): FelineGrimaceScaleValue {
    const answer = answers[this.field.name];
    if (answer === undefined) {
      return this.getDefaultValue();
    }

    const isValid = FelineGrimaceScaleValueSchema.safeParse(answer).success;
    if (!isValid) {
      throw new Error('Invalid answer for Cat Grimace Scale field');
    }
    return answer as FelineGrimaceScaleValue;
  }

  getDefaultValue(): FelineGrimaceScaleValue {
    return {};
  }

  getValueToSubmit(value: unknown): FelineGrimaceScaleValue {
    if (typeof value === 'undefined' || value === null) {
      return this.getDefaultValue();
    }

    const isValid = FelineGrimaceScaleValueSchema.safeParse(value).success;
    if (!isValid) {
      throw new Error('Invalid answer for Cat Grimace Scale field');
    }
    return value as FelineGrimaceScaleValue;
  }
}
